import React, { useState, useEffect, useRef } from 'react';
import { useEventData } from '../hooks/useEventData';
import { useFont } from '../../Design/FontContext';
import { formatTicketPrice, formatEventDate, formatEventTime, getEventTypeInfo } from '../hooks/useEventData';
import { getTextEffectStyles } from '../utils/textEffectsUtils';
import { getButtonEffectStyles } from '../utils/buttonEffectsUtils';
import { getResponsiveFontSize } from '../utils/responsiveFontUtils';
import DeleteModal from './DeleteModal';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import './WidgetStyle.css';
import './ResponsiveFonts.css';
import './EventInfoWidgetGuide.css';
import '../WidgetEditComponents/TextEffectsAnimations.css';

function appendPx(value) {
    if (typeof value === 'number') return `${value}px`;
    return value;
}

const EventInfoWidget = ({ data, isSelected, onRemove, isEditMode, onUpdate, onSelectWidget }) => {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isMobileLayout, setIsMobileLayout] = useState(false);
    const [currentBreakpoint, setCurrentBreakpoint] = useState('normal');
    const [refreshCounter, setRefreshCounter] = useState(0);
    const [isProcessingCheckout, setIsProcessingCheckout] = useState(false);
    const containerRef = useRef(null);
    const { eventData, loading, error } = useEventData(data.eventId, refreshCounter);
    
    // Firebase Auth & Functions
    const auth = getAuth();
    const user = auth.currentUser;
    const functions = getFunctions();
    
    const deleteLogo = '/img/delete.svg';
    const fonts = useFont();
    
    // Inline Editing System
    const [isInlineEditing, setIsInlineEditing] = useState(null);
    const [inlineValue, setInlineValue] = useState('');
    const widgetRef = useRef(null);

    // Responsive breakpoint detection
    useEffect(() => {
        const handleResize = () => {
            const width = window.innerWidth;
            if (width < 768) {
                setCurrentBreakpoint('mobile');
                setIsMobileLayout(true);
            } else if (width > 1200) {
                setCurrentBreakpoint('big');
                setIsMobileLayout(false);
            } else {
                setCurrentBreakpoint('normal');
                setIsMobileLayout(false);
            }
        };

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Event refresh handlers
    useEffect(() => {
        const handleFocus = () => {
            setRefreshCounter(prev => prev + 1);
        };

        const handleVisibilityChange = () => {
            if (!document.hidden) {
                setRefreshCounter(prev => prev + 1);
            }
        };

        const handleEventUpdate = (event) => {
            if (event.detail.eventId === data.eventId) {
                setRefreshCounter(prev => prev + 1);
            }
        };

        window.addEventListener('focus', handleFocus);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('eventUpdated', handleEventUpdate);

        return () => {
            window.removeEventListener('focus', handleFocus);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('eventUpdated', handleEventUpdate);
        };
    }, [data.eventId]);

    useEffect(() => {
        setRefreshCounter(prev => prev + 1);
    }, [data.eventId]);

    // Inline editing handlers
    const handleInlineEdit = (field, currentValue) => {
        if (!isEditMode) return;
        setIsInlineEditing(field);
        setInlineValue(currentValue);
    };

    const handleInlineSave = () => {
        if (onUpdate && isInlineEditing) {
            onUpdate(data.id, { [isInlineEditing]: inlineValue });
        }
        setIsInlineEditing(null);
        setInlineValue('');
    };

    const handleInlineCancel = () => {
        setIsInlineEditing(null);
        setInlineValue('');
    };

    // Outside click detection for inline editing
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (widgetRef.current && !widgetRef.current.contains(event.target) && isInlineEditing) {
                handleInlineSave();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isInlineEditing, inlineValue]);

    // Style functions
    function getBorderStyle() {
        if (isEditMode) {
            return isSelected ? '2px dashed #000' : '2px dashed transparent';
        }
        return data.border ? `${data.borderWidth}px solid ${data.borderColor}` : 'none';
    }

    const getResponsiveStyle = (baseProperty, suffix = '') => {
        return getResponsiveFontSize(data, baseProperty, currentBreakpoint, suffix);
    };

    const handleDelete = (event) => {
        onRemove(event, data.id);
        setShowDeleteModal(false);
    };

    // Helper Functions for Event Purchase
    const isFreeEvent = () => {
        return eventData?.price === 0 || eventData?.price === '0' || !eventData?.price;
    };

    // Handle Stripe checkout for paid events
    const handleStripeCheckout = async (ticketType = null) => {
        if (!eventData || isFreeEvent() || isProcessingCheckout) return;

        // Check if event has Stripe configuration
        if (!eventData.stripePriceId) {
            alert('This event is not properly configured for payments. Please contact support.');
            return;
        }

        setIsProcessingCheckout(true);

        try {
            console.log('🎟️ Creating event checkout session...');
            console.log('Event ID:', data.eventId);
            console.log('Stripe Price ID:', eventData.stripePriceId);
            console.log('Ticket Type:', ticketType);

            const createProductCheckoutSession = httpsCallable(functions, 'createProductCheckoutSession');
            
            const { data: sessionData } = await createProductCheckoutSession({
                userId: user ? user.uid : null,
                productId: data.eventId,
                priceId: eventData.stripePriceId,
                successUrl: eventData.successUrl || `${window.location.origin}/success?session_id={CHECKOUT_SESSION_ID}`,
                cancelUrl: eventData.cancelUrl || window.location.href,
                customerEmail: user ? user.email : null,
                metadata: {
                    ticketType: ticketType || 'general',
                    eventId: data.eventId
                }
            });

            console.log('Checkout session created:', sessionData);

            // Redirect to Stripe Checkout
            if (sessionData.checkoutUrl) {
                window.location.href = sessionData.checkoutUrl;
            } else {
                // Fallback to Stripe's redirect method
                const stripe = await import('@stripe/stripe-js').then(module => 
                    module.loadStripe('pk_live_51JtPI5EK7wspITU4metLteANz6fXOGY4vIvFtRdJYYg3oYt0W9M0mipHroPArKiCQPaAeU7sZvgH17wU0FMYN69200bLf83Oq7')
                );
                
                const { error } = await stripe.redirectToCheckout({ 
                    sessionId: sessionData.sessionId 
                });

                if (error) {
                    console.error("Stripe checkout error:", error);
                    alert('Payment processing error. Please try again.');
                }
            }
        } catch (error) {
            console.error("Checkout session error:", error);
            alert('Unable to process payment. Please try again or contact support.');
        } finally {
            setIsProcessingCheckout(false);
        }
    };

    // Image Filter Effects (same as ProductInfoWidget)
    const getImageEffect = (effect) => {
        switch (effect) {
            case 'blur': return { filter: 'blur(3px)', gradient: null };
            case 'blur-light': return { filter: 'blur(1px)', gradient: null };
            case 'blur-heavy': return { filter: 'blur(5px)', gradient: null };
            case 'grayscale': return { filter: 'grayscale(100%)', gradient: null };
            case 'sepia': return { filter: 'sepia(100%)', gradient: null };
            case 'top-fade': return { filter: 'none', gradient: 'linear-gradient(to bottom, rgba(0,0,0,0.6), transparent)' };
            case 'bottom-fade': return { filter: 'none', gradient: 'linear-gradient(to top, rgba(0,0,0,0.6), transparent)' };
            case 'left-fade': return { filter: 'none', gradient: 'linear-gradient(to right, rgba(0,0,0,0.6), transparent)' };
            case 'right-fade': return { filter: 'none', gradient: 'linear-gradient(to left, rgba(0,0,0,0.6), transparent)' };
            case 'center-glow': return { filter: 'none', gradient: 'radial-gradient(circle at center, transparent 20%, rgba(0,0,0,0.6))' };
            case 'dark-vignette': return { filter: 'none', gradient: 'radial-gradient(circle, transparent 30%, rgba(0,0,0,0.7))' };
            case 'light-vignette': return { filter: 'none', gradient: 'radial-gradient(circle, transparent 30%, rgba(255,255,255,0.7))' };
            default: return { filter: 'none', gradient: null };
        }
    };

    // Premium Ticket Style System
    const getTicketStyle = (ticketIndex, styleType) => {
        const styles = {
            golden: {
                background: 'linear-gradient(135deg, #fffef7 0%, #fef9e7 10%, #fef3c7 20%, #fde047 35%, #eab308 50%, #ca8a04 65%, #a16207 80%, #854d0e 95%, #713f12 100%)',
                border: '2px solid #ca8a04',
                boxShadow: '0 12px 40px rgba(202, 138, 4, 0.5), 0 8px 24px rgba(234, 179, 8, 0.4), inset 0 2px 0 rgba(255, 255, 255, 0.4), inset 0 -1px 0 rgba(202, 138, 4, 0.3)',
                color: '#713f12',
                priceColor: '#fffef7',
                priceBg: 'linear-gradient(135deg, #854d0e 0%, #a16207 100%)',
                perforation: '#ca8a04',
                label: 'GOLDEN TICKET',
                icon: 'fas fa-crown',
                transform: 'perspective(1000px) rotateX(3deg) rotateY(-2deg)',
                animation: 'goldenShimmer 3s ease-in-out infinite',
                textShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
                backdropFilter: 'blur(8px)',
                // Text colors for content
                titleColor: '#713f12',
                descriptionColor: '#92400e',
                benefitColor: '#a16207'
            },
            silver: {
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 10%, #f1f5f9 20%, #e2e8f0 35%, #cbd5e1 50%, #94a3b8 65%, #64748b 80%, #475569 95%, #334155 100%)',
                border: '2px solid #64748b',
                boxShadow: '0 12px 40px rgba(100, 116, 139, 0.4), 0 8px 24px rgba(148, 163, 184, 0.3), inset 0 2px 0 rgba(255, 255, 255, 0.7), inset 0 -1px 0 rgba(100, 116, 139, 0.2)',
                color: '#1e293b',
                priceColor: '#ffffff',
                priceBg: 'linear-gradient(135deg, #334155 0%, #475569 100%)',
                perforation: '#64748b',
                label: 'SILVER TICKET',
                icon: 'fas fa-medal',
                transform: 'perspective(1000px) rotateX(2deg) rotateY(1deg)',
                animation: 'silverShimmer 4s ease-in-out infinite',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.08)',
                backdropFilter: 'blur(8px)',
                // Text colors for content
                titleColor: '#1e293b',
                descriptionColor: '#475569',
                benefitColor: '#64748b'
            },
            premium: {
                background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 10%, #e9d5ff 20%, #d8b4fe 35%, #c084fc 50%, #a855f7 65%, #9333ea 80%, #7c3aed 95%, #6d28d9 100%)',
                border: '2px solid #8b5cf6',
                boxShadow: '0 12px 40px rgba(139, 92, 246, 0.5), 0 8px 24px rgba(168, 85, 247, 0.4), inset 0 2px 0 rgba(255, 255, 255, 0.4), inset 0 -1px 0 rgba(139, 92, 246, 0.3)',
                color: '#581c87',
                priceColor: '#faf5ff',
                priceBg: 'linear-gradient(135deg, #6d28d9 0%, #7c3aed 100%)',
                perforation: '#a855f7',
                label: 'PREMIUM TICKET',
                icon: 'fas fa-gem',
                transform: 'perspective(1000px) rotateX(2deg) rotateY(-1deg)',
                animation: 'premiumGlow 2s ease-in-out infinite',
                textShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
                backdropFilter: 'blur(8px)',
                // Text colors for content
                titleColor: '#581c87',
                descriptionColor: '#6d28d9',
                benefitColor: '#7c3aed'
            },
            vip: {
                background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 10%, #fecaca 20%, #fca5a5 35%, #f87171 50%, #ef4444 65%, #dc2626 80%, #b91c1c 95%, #991b1b 100%)',
                border: '2px solid #dc2626',
                boxShadow: '0 12px 40px rgba(220, 38, 38, 0.5), 0 8px 24px rgba(239, 68, 68, 0.4), inset 0 2px 0 rgba(255, 255, 255, 0.3), inset 0 -1px 0 rgba(220, 38, 38, 0.3)',
                color: '#7f1d1d',
                priceColor: '#fef2f2',
                priceBg: 'linear-gradient(135deg, #991b1b 0%, #b91c1c 100%)',
                perforation: '#ef4444',
                label: 'VIP TICKET',
                icon: 'fas fa-star',
                transform: 'perspective(1000px) rotateX(3deg) rotateY(2deg)',
                animation: 'vipPulse 1.5s ease-in-out infinite',
                textShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
                backdropFilter: 'blur(8px)',
                // Text colors for content
                titleColor: '#7f1d1d',
                descriptionColor: '#991b1b',
                benefitColor: '#b91c1c'
            },
            standard: {
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 25%, #f1f5f9 50%, #e2e8f0 75%, #cbd5e1 100%)',
                border: '2px solid rgba(0, 0, 0, 0.08)',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
                color: data.titleColor,
                priceColor: 'white',
                priceBg: data.ticketPriceColor,
                perforation: data.ticketPriceColor,
                label: 'EVENT TICKET',
                icon: 'fas fa-ticket-alt',
                transform: 'perspective(1000px) rotateX(2deg)',
                animation: 'none',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
                backdropFilter: 'blur(8px)',
                // Text colors for content
                titleColor: data.titleColor,
                descriptionColor: data.descriptionColor,
                benefitColor: data.locationColor
            }
        };
        
        return styles[styleType] || styles.standard;
    };


    // Premium widget styles - Stripe-inspired
    const widgetStyle = {
        backgroundColor: data.backgroundColor,
        borderRadius: '20px',
        marginTop: `${data.marginTop}px`,
        marginBottom: `${data.marginBottom}px`,
        padding: isMobileLayout ? '32px 24px' : '48px',
        border: getBorderStyle() !== 'none' ? getBorderStyle() : '1px solid rgba(0, 0, 0, 0.06)',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08), 0 4px 16px rgba(0, 0, 0, 0.04)',
        minHeight: data.layout === 'hero' ? `${data.height}px` : 'auto',
        cursor: isEditMode ? 'pointer' : 'default',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        backdropFilter: 'blur(8px)',
        maxWidth: '1200px',
        width: '100%',
        margin: `${data.marginTop}px auto ${data.marginBottom}px auto`
    };

    // Remove old background system - will be replaced by proper image effect system

    // Loading state
    if (loading) {
        return (
            <div style={widgetStyle} ref={widgetRef}>
                {isEditMode && (
                    <button className="delete_button" onClick={() => setShowDeleteModal(true)}>
                        <img className="delete_logo" src={deleteLogo} alt="Löschen" />
                    </button>
                )}
                <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    minHeight: '200px',
                    color: '#64748b' 
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                            width: '20px',
                            height: '20px',
                            border: '2px solid #e2e8f0',
                            borderTop: '2px solid #3b82f6',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                        }}></div>
                        <span>Loading event...</span>
                    </div>
                </div>
            </div>
        );
    }

    // No Event Selected State
    if (!data.eventId || !eventData) {
        return (
            <div className="event-info-widget no-event-selected" style={widgetStyle} ref={widgetRef}>
                {isEditMode && (
                    <div>
                        <button className="delete_button" onClick={() => setShowDeleteModal(true)}>
                            <img className="delete_logo" src={deleteLogo} alt="Löschen" />
                        </button>
                        {showDeleteModal && (
                            <DeleteModal
                                isOpen={showDeleteModal}
                                onClose={() => setShowDeleteModal(false)}
                                onConfirm={handleDelete}
                            />
                        )}
                    </div>
                )}
                <div className="event-selection-guide">
                    <div className="guide-icon">
                        <div className="icon-circle">
                            <i className="fas fa-calendar-alt"></i>
                        </div>
                        <div className="pulse-ring"></div>
                    </div>
                    <div className="guide-content">
                        <h3 className="guide-title">Select Your Event</h3>
                        <p className="guide-description">
                            Choose an event from your collection to showcase it beautifully with this widget.
                        </p>
                        <div className="guide-steps">
                            <div className="step">
                                <span className="step-number">1</span>
                                <span className="step-text">Open the Event tab in the widget settings</span>
                            </div>
                            <div className="step">
                                <span className="step-number">2</span>
                                <span className="step-text">Select your event from the dropdown</span>
                            </div>
                            <div className="step">
                                <span className="step-number">3</span>
                                <span className="step-text">Customize the display options</span>
                            </div>
                        </div>
                        <div className="guide-note">
                            <i className="fas fa-info-circle"></i>
                            <span>This widget works with all your Events from the Sales Platform</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const eventTypeInfo = getEventTypeInfo(eventData.contentData?.eventType);


    return (
        <div 
            className="event-info-widget"
            style={widgetStyle} 
            ref={widgetRef}
            onClick={() => isEditMode && onSelectWidget && onSelectWidget(data.id)}
        >
            {/* Background Image Container with Effects (like ProductInfoWidget) */}
            {data.backgroundImage && (
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 1
                }}>
                    <img 
                        src={data.backgroundImage}
                        alt="Background"
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            opacity: data.backgroundImageOpacity > 1 ? 
                                (parseFloat(data.backgroundImageOpacity) / 100) : // Prozent-Wert (10-100) zu Dezimal
                                (parseFloat(data.backgroundImageOpacity) || 0.85), // Bereits Dezimal-Wert (0.1-1.0)
                            filter: data.imageEffect ? getImageEffect(data.imageEffect).filter : 'none'
                        }}
                    />
                    
                    {/* Gradient/Effect Overlay */}
                    {data.imageEffect && getImageEffect(data.imageEffect).gradient && (
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: getImageEffect(data.imageEffect).gradient
                        }} />
                    )}
                </div>
            )}
            
            {/* Delete Button */}
            {isEditMode && (
                <button 
                    className="delete_button" 
                    onClick={(e) => {
                        e.stopPropagation();
                        setShowDeleteModal(true);
                    }}
                    style={{ zIndex: 10 }}
                >
                    <img className="delete_logo" src={deleteLogo} alt="Löschen" />
                </button>
            )}

            {/* Delete Modal */}
            {isEditMode && showDeleteModal && (
                <DeleteModal
                    isOpen={showDeleteModal}
                    onClose={() => setShowDeleteModal(false)}
                    onConfirm={handleDelete}
                />
            )}

            {/* Premium 2-Column Layout */}
            <div style={{ 
                position: 'relative', 
                zIndex: 2, 
                width: '100%',
                display: 'grid',
                gridTemplateColumns: isMobileLayout ? '1fr' : 
                                   data.layout === 'inline' && data.showEventImage && data.eventImage ? '1fr 400px' :
                                   '1fr',
                gap: isMobileLayout ? '32px' : '48px',
                alignItems: 'start'
            }}>
                
                {/* Left Column - Main Content */}
                <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: '40px'
                }}>
                    
                    {/* Premium Header Section */}
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '24px'
                    }}>
                        {/* Event Type Badge - Stripe Style */}
                        {data.showEventType && eventData.contentData?.eventType && (
                            <div style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '10px',
                                backgroundColor: `${eventTypeInfo.color}08`,
                                color: eventTypeInfo.color,
                                padding: '10px 16px',
                                borderRadius: '12px',
                                fontSize: `${data.eventTypeSize + 1}px`,
                                fontWeight: '600',
                                width: 'fit-content',
                                border: `1px solid ${eventTypeInfo.color}15`,
                                backdropFilter: 'blur(8px)',
                                letterSpacing: '0.3px'
                            }}>
                                <i className={eventData.contentData?.eventType === 'digital' ? 'fas fa-laptop' : 'fas fa-calendar-alt'} style={{ fontSize: '16px' }}></i>
                                <span>{eventTypeInfo.label}</span>
                            </div>
                        )}

                        {/* Title - Uses Event Data (NOT editable inline) */}
                        {data.showTitle && (
                            <h1
                                style={{
                                    ...getResponsiveStyle('titleSize'),
                                    fontWeight: getResponsiveStyle('titleWeight', 'Weight'),
                                    color: data.titleColor,
                                    fontFamily: data.titleFont || fonts.globalBigFont,
                                    margin: '0',
                                    lineHeight: '1.1',
                                    letterSpacing: '-0.02em',
                                    ...getTextEffectStyles(data.titleEffect)
                                }}
                            >
                                {eventData.title}
                            </h1>
                        )}

                        {/* Description - Uses Event Data (NOT editable inline) */}
                        {data.showDescription && (
                            <p
                                style={{
                                    ...getResponsiveStyle('descriptionSize'),
                                    fontWeight: getResponsiveStyle('descriptionWeight', 'Weight'),
                                    color: data.descriptionColor,
                                    fontFamily: data.descriptionFont || fonts.globalSmallFont,
                                    margin: '0',
                                    lineHeight: '1.7',
                                    opacity: 0.85,
                                    ...getTextEffectStyles(data.descriptionEffect)
                                }}
                            >
                                {eventData.description}
                            </p>
                        )}

                        {/* Location/Meeting Info - Premium Card */}
                        {data.showLocation && (
                            <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '16px',
                                padding: '20px 24px',
                                backgroundColor: 'rgba(255, 255, 255, 0.6)',
                                borderRadius: '16px',
                                border: '1px solid rgba(0, 0, 0, 0.06)',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                                backdropFilter: 'blur(8px)'
                            }}>
                                <div style={{
                                    width: '48px',
                                    height: '48px',
                                    borderRadius: '12px',
                                    backgroundColor: `${data.locationColor}12`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '18px',
                                    border: `1px solid ${data.locationColor}20`,
                                    color: data.locationColor
                                }}>
                                    <i className={eventData.contentData?.eventType === 'digital' ? 'fas fa-laptop' : 'fas fa-map-marker-alt'}></i>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                                    <span style={{
                                        fontSize: '13px',
                                        fontWeight: '600',
                                        color: data.locationColor,
                                        opacity: 0.7,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.8px'
                                    }}>
                                        {eventData.contentData?.eventType === 'digital' ? 'Online Event' : 'Location'}
                                    </span>
                                    <span style={{
                                        fontSize: `${data.locationSize + 3}px`,
                                        fontWeight: '500',
                                        color: data.locationColor,
                                        lineHeight: '1.4'
                                    }}>
                                        {eventData.contentData?.eventType === 'digital' ? 
                                            (eventData.contentData?.meetingUrl ? 'Virtual Meeting Room' : 'Digital Event') :
                                            (eventData.contentData?.eventAddress || 'Location TBD')
                                        }
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Premium Event Schedule */}
                    {data.showEventDays && eventData.contentData?.eventDays?.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                <div style={{
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '12px',
                                    backgroundColor: `${data.dateColor}12`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: `1px solid ${data.dateColor}20`,
                                    color: data.dateColor
                                }}>
                                    <i className="fas fa-calendar-alt" style={{ fontSize: '16px' }}></i>
                                </div>
                                <h2 
                                    style={{ 
                                        margin: '0', 
                                        fontSize: '24px', 
                                        fontWeight: '700',
                                        color: data.dateColor,
                                        letterSpacing: '-0.02em',
                                        cursor: isEditMode ? 'text' : 'default',
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        border: 'none',
                                        transition: 'all 0.2s ease'
                                    }}
                                    onClick={() => isEditMode && handleInlineEdit('scheduleTitle', data.scheduleTitle || 'Event Schedule')}
                                    onMouseEnter={(e) => {
                                        if (isEditMode) e.target.style.cursor = 'text';
                                    }}
                                    onMouseLeave={(e) => {
                                        if (isEditMode) e.target.style.cursor = 'text';
                                    }}
                                    title={isEditMode ? "Click to edit" : undefined}
                                >
                                    {isInlineEditing === 'scheduleTitle' ? (
                                        <input
                                            type="text"
                                            value={inlineValue}
                                            onChange={(e) => setInlineValue(e.target.value)}
                                            onKeyPress={(e) => e.key === 'Enter' && handleInlineSave()}
                                            onBlur={handleInlineSave}
                                            style={{
                                                fontSize: '24px',
                                                fontWeight: '700',
                                                color: data.dateColor,
                                                letterSpacing: '-0.02em',
                                                background: 'transparent',
                                                border: 'none',
                                                outline: 'none',
                                                margin: '0',
                                                padding: '0',
                                                width: '100%',
                                                fontFamily: 'inherit'
                                            }}
                                            autoFocus
                                        />
                                    ) : (
                                        data.showScheduleTitle !== false && (data.scheduleTitle || 'Event Schedule')
                                    )}
                                </h2>
                            </div>
                            
                            <div style={{ 
                                display: 'grid',
                                gridTemplateColumns: isMobileLayout ? '1fr' : 'repeat(auto-fit, minmax(400px, 1fr))',
                                gap: '24px'
                            }}>
                                {eventData.contentData.eventDays.slice(0, data.maxEventDaysToShow || 3).map((eventDay, index) => (
                                    <div key={index} style={{
                                        padding: '28px',
                                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                        borderRadius: '20px',
                                        border: '1px solid rgba(0, 0, 0, 0.06)',
                                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
                                        transition: 'all 0.3s ease',
                                        position: 'relative',
                                        overflow: 'hidden'
                                    }}>
                                        {/* Day Header */}
                                        <div style={{ 
                                            display: 'flex', 
                                            justifyContent: 'space-between', 
                                            alignItems: 'flex-start', 
                                            marginBottom: '20px',
                                            flexWrap: 'wrap',
                                            gap: '12px'
                                        }}>
                                            <h3 style={{ 
                                                margin: '0', 
                                                fontSize: `${data.dateSize + 4}px`,
                                                fontWeight: '700',
                                                color: data.dateColor,
                                                letterSpacing: '-0.01em'
                                            }}>
                                                {eventDay.title || `Day ${index + 1}`}
                                            </h3>
                                            <div style={{
                                                padding: '8px 16px',
                                                backgroundColor: `${data.dateColor}10`,
                                                borderRadius: '12px',
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                color: data.dateColor,
                                                border: `1px solid ${data.dateColor}20`,
                                                backdropFilter: 'blur(4px)'
                                            }}>
                                                {formatEventDate(eventDay.date)}
                                            </div>
                                        </div>
                                        
                                        {eventDay.description && (
                                            <p style={{ 
                                                margin: '0 0 20px 0', 
                                                fontSize: '15px',
                                                color: data.descriptionColor,
                                                opacity: 0.8,
                                                lineHeight: '1.6'
                                            }}>
                                                {eventDay.description}
                                            </p>
                                        )}
                                        
                                        {/* Premium Time Slots */}
                                        {data.showTimeSlots && eventDay.timeSlots?.length > 0 && (
                                            <div style={{ 
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '12px'
                                            }}>
                                                {eventDay.timeSlots.map((slot, slotIndex) => (
                                                    <div key={slotIndex} style={{
                                                        padding: '16px 20px',
                                                        backgroundColor: 'rgba(0, 0, 0, 0.03)',
                                                        borderRadius: '12px',
                                                        border: '1px solid rgba(0, 0, 0, 0.06)',
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        transition: 'all 0.2s ease'
                                                    }}>
                                                        <span style={{
                                                            fontSize: '15px',
                                                            fontWeight: '600',
                                                            color: data.titleColor
                                                        }}>
                                                            {slot.title}
                                                        </span>
                                                        <div style={{
                                                            padding: '4px 12px',
                                                            backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                                            borderRadius: '8px',
                                                            fontSize: '13px',
                                                            color: data.locationColor,
                                                            fontWeight: '600',
                                                            fontFamily: 'monospace',
                                                            letterSpacing: '0.5px'
                                                        }}>
                                                            {formatEventTime(slot.startTime)} - {formatEventTime(slot.endTime)}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Premium Custom Features */}
                    {data.showCustomFeatures && data.customFeatures?.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '8px',
                                    backgroundColor: `${data.featuresIconColor}15`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: data.featuresIconColor
                                }}>
                                    <i className="fas fa-star" style={{ fontSize: '14px' }}></i>
                                </div>
                                <h3 
                                    style={{ 
                                        margin: '0', 
                                        fontSize: '20px', 
                                        fontWeight: '600',
                                        color: data.titleColor,
                                        letterSpacing: '-0.01em',
                                        cursor: isEditMode ? 'text' : 'default',
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        transition: 'all 0.2s ease'
                                    }}
                                    onClick={() => isEditMode && handleInlineEdit('featuresTitle', data.featuresTitle || 'What\'s Included')}
                                    title={isEditMode ? "Click to edit" : undefined}
                                >
                                    {isInlineEditing === 'featuresTitle' ? (
                                        <input
                                            type="text"
                                            value={inlineValue}
                                            onChange={(e) => setInlineValue(e.target.value)}
                                            onKeyPress={(e) => e.key === 'Enter' && handleInlineSave()}
                                            onBlur={handleInlineSave}
                                            style={{
                                                fontSize: '20px',
                                                fontWeight: '600',
                                                color: data.titleColor,
                                                letterSpacing: '-0.01em',
                                                background: 'transparent',
                                                border: 'none',
                                                outline: 'none',
                                                margin: '0',
                                                padding: '0',
                                                width: '100%',
                                                fontFamily: 'inherit'
                                            }}
                                            autoFocus
                                        />
                                    ) : (
                                        data.showFeaturesTitle !== false && (data.featuresTitle || 'What\'s Included')
                                    )}
                                </h3>
                            </div>
                            
                            <div style={{ 
                                display: data.featuresLayout === 'column' ? 'flex' : 'grid',
                                flexDirection: data.featuresLayout === 'column' ? 'column' : undefined,
                                gridTemplateColumns: data.featuresLayout === 'column' ? undefined : 
                                                   (isMobileLayout ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))'),
                                gap: data.featuresLayout === 'column' ? '12px' : '16px'
                            }}>
                                {data.customFeatures.map((feature, index) => (
                                    <div key={index} style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: '16px',
                                        padding: data.featuresLayout === 'column' ? '12px 16px' : '16px 20px',
                                        backgroundColor: 'rgba(255, 255, 255, 0.6)',
                                        borderRadius: data.featuresLayout === 'column' ? '8px' : '12px',
                                        border: '1px solid rgba(0, 0, 0, 0.06)',
                                        boxShadow: data.featuresLayout === 'column' ? 
                                                  '0 1px 4px rgba(0, 0, 0, 0.04)' : 
                                                  '0 2px 8px rgba(0, 0, 0, 0.04)',
                                        transition: 'all 0.2s ease',
                                        width: data.featuresLayout === 'column' ? '100%' : undefined
                                    }}>
                                        <div style={{
                                            width: '28px',
                                            height: '28px',
                                            borderRadius: '50%',
                                            backgroundColor: data.featuresIconColor,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                            boxShadow: `0 2px 8px ${data.featuresIconColor}40`
                                        }}>
                                            <i className="fas fa-check" style={{ 
                                                color: 'white', 
                                                fontSize: '12px',
                                                fontWeight: '700'
                                            }}></i>
                                        </div>
                                        <span style={{
                                            fontSize: '15px',
                                            color: data.descriptionColor,
                                            fontWeight: '500',
                                            lineHeight: '1.4'
                                        }}>
                                            {feature.text}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                </div>

                {/* Right Column - Tickets & Event Image */}
                {!isMobileLayout && (
                    <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: '32px',
                        position: 'sticky',
                        top: '20px'
                    }}>
                        
                        {/* Event Image - Premium Display */}
                        {data.showEventImage && (
                            data.eventImage ? (
                            <div style={{
                                borderRadius: '20px',
                                overflow: 'hidden',
                                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                                border: '1px solid rgba(0, 0, 0, 0.06)'
                            }}>
                                <img 
                                    src={data.eventImage}
                                    alt={eventData.title}
                                    style={{
                                        width: '100%',
                                        height: '240px',
                                        objectFit: 'cover',
                                        display: 'block'
                                    }}
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                    }}
                                />
                            </div>
                            ) : (
                                <div style={{
                                    borderRadius: '20px',
                                    padding: '40px',
                                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                    border: '2px dashed rgba(0, 0, 0, 0.1)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '12px',
                                    textAlign: 'center'
                                }}>
                                    <div style={{
                                        width: '48px',
                                        height: '48px',
                                        borderRadius: '12px',
                                        backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '16px',
                                        color: '#9ca3af'
                                    }}>
                                        <i className="fas fa-image"></i>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280', marginBottom: '4px' }}>
                                            No Event Image
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                                            Upload an image in the widget settings
                                        </div>
                                    </div>
                                </div>
                            )
                        )}

                        {/* Premium Tickets */}
                        {data.showTickets && eventData.contentData?.tickets?.length > 0 && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <div style={{
                                        width: '32px',
                                        height: '32px',
                                        borderRadius: '8px',
                                        backgroundColor: `${data.ticketPriceColor}15`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: data.ticketPriceColor
                                    }}>
                                        <i className="fas fa-ticket-alt" style={{ fontSize: '14px' }}></i>
                                    </div>
                                    <h3 
                                        style={{ 
                                            margin: '0', 
                                            fontSize: '20px', 
                                            fontWeight: '600',
                                            color: data.titleColor,
                                            letterSpacing: '-0.01em',
                                            cursor: isEditMode ? 'text' : 'default',
                                            padding: '4px 8px',
                                            borderRadius: '4px',
                                            transition: 'all 0.2s ease'
                                        }}
                                        onClick={() => isEditMode && handleInlineEdit('ticketsTitle', data.ticketsTitle || 'Tickets')}
                                        title={isEditMode ? "Click to edit" : undefined}
                                    >
                                        {isInlineEditing === 'ticketsTitle' ? (
                                            <input
                                                type="text"
                                                value={inlineValue}
                                                onChange={(e) => setInlineValue(e.target.value)}
                                                onKeyPress={(e) => e.key === 'Enter' && handleInlineSave()}
                                                onBlur={handleInlineSave}
                                                style={{
                                                    fontSize: '20px',
                                                    fontWeight: '600',
                                                    color: data.titleColor,
                                                    letterSpacing: '-0.01em',
                                                    background: 'transparent',
                                                    border: 'none',
                                                    outline: 'none',
                                                    margin: '0',
                                                    padding: '0',
                                                    width: '100%',
                                                    fontFamily: 'inherit'
                                                }}
                                                autoFocus
                                            />
                                        ) : (
                                            data.showTicketsTitle !== false && (data.ticketsTitle || 'Tickets')
                                        )}
                                    </h3>
                                </div>
                                
                                <div style={{ 
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '16px'
                                }}>
                                    {eventData.contentData.tickets.slice(0, data.maxTicketsToShow || 4).map((ticket, index) => {
                                        const ticketStyle = getTicketStyle(index, data.ticketStyles?.[index] || 'standard');
                                        
                                        return (
                                        <div key={index} style={{
                                            padding: '0',
                                            background: ticketStyle.background,
                                            border: ticketStyle.border,
                                            borderRadius: '16px',
                                            boxShadow: ticketStyle.boxShadow,
                                            transition: 'all 0.3s ease',
                                            position: 'relative',
                                            overflow: 'hidden',
                                            transform: ticketStyle.transform,
                                            backdropFilter: 'blur(12px)',
                                            animation: ticketStyle.animation
                                        }}>
                                            {/* Ticket Style Indicator */}
                                            {ticketStyle.animation !== 'none' && (
                                                <div style={{
                                                    position: 'absolute',
                                                    top: '12px',
                                                    right: '12px',
                                                    width: '24px',
                                                    height: '24px',
                                                    borderRadius: '50%',
                                                    backgroundColor: ticketStyle.priceBg,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    zIndex: 3,
                                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
                                                }}>
                                                    <i className={ticketStyle.icon} style={{ 
                                                        fontSize: '10px', 
                                                        color: ticketStyle.priceColor 
                                                    }}></i>
                                                </div>
                                            )}

                                            {/* Ticket Perforated Edge */}
                                            <div style={{
                                                position: 'absolute',
                                                left: '20px',
                                                top: '0',
                                                bottom: '0',
                                                width: '2px',
                                                background: `repeating-linear-gradient(
                                                    to bottom,
                                                    transparent 0px,
                                                    transparent 8px,
                                                    ${ticketStyle.perforation}40 8px,
                                                    ${ticketStyle.perforation}40 12px
                                                )`,
                                                zIndex: 2
                                            }}></div>

                                            {/* Ticket Main Content */}
                                            <div style={{ padding: '24px 24px 24px 40px' }}>
                                                {/* Ticket Header */}
                                                <div style={{ 
                                                    display: 'flex', 
                                                    justifyContent: 'space-between', 
                                                    alignItems: 'flex-start', 
                                                    marginBottom: '16px',
                                                    gap: '12px'
                                                }}>
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{
                                                            fontSize: '11px',
                                                            fontWeight: '600',
                                                            color: ticketStyle.color,
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '1px',
                                                            marginBottom: '4px',
                                                            opacity: 0.8
                                                        }}>
                                                            {ticketStyle.label}
                                                        </div>
                                                        <h4 style={{ 
                                                            margin: '0', 
                                                            fontSize: '18px',
                                                            fontWeight: '700',
                                                            color: ticketStyle.titleColor || ticketStyle.color,
                                                            letterSpacing: '-0.01em',
                                                            textShadow: ticketStyle.textShadow
                                                        }}>
                                                            {ticket.name}
                                                        </h4>
                                                    </div>
                                                    
                                                    {/* Price Badge - Styled per Ticket Type */}
                                                    <div style={{
                                                        padding: '12px 20px',
                                                        backgroundColor: ticketStyle.priceBg,
                                                        borderRadius: '8px',
                                                        position: 'relative',
                                                        boxShadow: `0 4px 12px ${ticketStyle.priceBg}60`
                                                    }}>
                                                        <div style={{
                                                            position: 'absolute',
                                                            left: '-6px',
                                                            top: '50%',
                                                            transform: 'translateY(-50%)',
                                                            width: '12px',
                                                            height: '12px',
                                                            backgroundColor: data.backgroundColor || '#ffffff',
                                                            borderRadius: '50%',
                                                            border: '1px solid rgba(0, 0, 0, 0.06)'
                                                        }}></div>
                                                        <div style={{
                                                            position: 'absolute',
                                                            right: '-6px',
                                                            top: '50%',
                                                            transform: 'translateY(-50%)',
                                                            width: '12px',
                                                            height: '12px',
                                                            backgroundColor: data.backgroundColor || '#ffffff',
                                                            borderRadius: '50%',
                                                            border: '1px solid rgba(0, 0, 0, 0.06)'
                                                        }}></div>
                                                        <span style={{
                                                            fontSize: `${data.ticketPriceSize - 2}px`,
                                                            fontWeight: data.ticketPriceWeight,
                                                            color: ticketStyle.priceColor,
                                                            textShadow: ticketStyle.color === '#ffffff' ? '0 1px 2px rgba(0, 0, 0, 0.2)' : 'none'
                                                        }}>
                                                            {formatTicketPrice(ticket.price, ticket.currency)}
                                                        </span>
                                                    </div>
                                                </div>
                                            
                                                {ticket.description && (
                                                    <p style={{ 
                                                        margin: '0 0 20px 0', 
                                                        fontSize: '14px',
                                                        color: ticketStyle.descriptionColor || data.descriptionColor,
                                                        opacity: 0.8,
                                                        lineHeight: '1.6',
                                                        fontStyle: 'italic',
                                                        textShadow: ticketStyle.textShadow
                                                    }}>
                                                        {ticket.description}
                                                    </p>
                                                )}
                                                
                                                {ticket.benefits?.length > 0 && (
                                                    <div style={{ 
                                                        display: 'flex', 
                                                        flexDirection: 'column', 
                                                        gap: '10px',
                                                        marginBottom: '20px',
                                                        padding: '16px',
                                                        backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                                        borderRadius: '12px',
                                                        border: '1px solid rgba(0, 0, 0, 0.04)'
                                                    }}>
                                                        <div style={{
                                                            fontSize: '12px',
                                                            fontWeight: '600',
                                                            color: data.titleColor,
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.5px',
                                                            marginBottom: '8px'
                                                        }}>
                                                            {data.showIncludesTitle !== false && (data.includesTitle || 'INCLUDES')}
                                                        </div>
                                                        {ticket.benefits.map((benefit, benefitIndex) => (
                                                            <div key={benefitIndex} style={{ 
                                                                fontSize: '13px',
                                                                color: data.locationColor,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '10px'
                                                            }}>
                                                                <span style={{
                                                                    width: '18px',
                                                                    height: '18px',
                                                                    borderRadius: '50%',
                                                                    backgroundColor: data.featuresIconColor,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    fontSize: '9px',
                                                                    color: 'white',
                                                                    fontWeight: '700',
                                                                    boxShadow: `0 2px 4px ${data.featuresIconColor}40`
                                                                }}>
                                                                    <i className="fas fa-check"></i>
                                                                </span>
                                                                <span style={{ 
                                                                    fontWeight: '500',
                                                                    color: ticketStyle.benefitColor || data.locationColor,
                                                                    textShadow: ticketStyle.textShadow
                                                                }}>{benefit}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                                
                                                {/* Ticket Availability Indicator */}
                                                {data.showTicketAvailability && (ticket.availability || ticket.sold !== undefined) && (
                                                    <div style={{
                                                        marginTop: '16px',
                                                        padding: '12px 16px',
                                                        backgroundColor: 'rgba(0, 0, 0, 0.03)',
                                                        borderRadius: '10px',
                                                        border: '1px solid rgba(0, 0, 0, 0.06)'
                                                    }}>
                                                        <div style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            marginBottom: '8px'
                                                        }}>
                                                            <span style={{
                                                                fontSize: '12px',
                                                                fontWeight: '600',
                                                                color: ticketStyle.color,
                                                                textTransform: 'uppercase',
                                                                letterSpacing: '0.5px'
                                                            }}>
                                                                {data.showAvailabilityTitle !== false && (data.availabilityTitle || 'Availability')}
                                                            </span>
                                                            <span style={{
                                                                fontSize: '13px',
                                                                fontWeight: '700',
                                                                color: ticketStyle.color
                                                            }}>
                                                                {ticket.sold || 0} / {ticket.availability || 100} sold
                                                            </span>
                                                        </div>
                                                        
                                                        {/* Progress Bar */}
                                                        <div style={{
                                                            width: '100%',
                                                            height: '8px',
                                                            backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                                            borderRadius: '4px',
                                                            overflow: 'hidden',
                                                            position: 'relative'
                                                        }}>
                                                            <div style={{
                                                                width: `${((ticket.sold || 0) / (ticket.availability || 100)) * 100}%`,
                                                                height: '100%',
                                                                background: `linear-gradient(90deg, ${ticketStyle.perforation}, ${ticketStyle.priceBg})`,
                                                                borderRadius: '4px',
                                                                transition: 'width 0.3s ease',
                                                                position: 'relative'
                                                            }}>
                                                                {/* Animated Progress Indicator */}
                                                                <div style={{
                                                                    position: 'absolute',
                                                                    top: 0,
                                                                    right: 0,
                                                                    width: '4px',
                                                                    height: '100%',
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.6)',
                                                                    animation: 'progressPulse 2s ease-in-out infinite'
                                                                }}></div>
                                                            </div>
                                                        </div>
                                                        
                                                        {/* Availability Status */}
                                                        <div style={{
                                                            marginTop: '6px',
                                                            fontSize: '11px',
                                                            fontWeight: '500',
                                                            color: ticketStyle.color,
                                                            opacity: 0.7,
                                                            textAlign: 'center'
                                                        }}>
                                                            {(() => {
                                                                const percentage = ((ticket.sold || 0) / (ticket.availability || 100)) * 100;
                                                                if (percentage >= 90) return (
                                                                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                                                        <i className="fas fa-fire" style={{ fontSize: '10px' }}></i>
                                                                        {data.almostSoldOutText || 'Almost Sold Out!'}
                                                                    </span>
                                                                );
                                                                if (percentage >= 70) return (
                                                                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                                                        <i className="fas fa-bolt" style={{ fontSize: '10px' }}></i>
                                                                        {data.sellingFastText || 'Selling Fast!'}
                                                                    </span>
                                                                );
                                                                if (percentage >= 50) return (
                                                                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                                                        <i className="fas fa-chart-line" style={{ fontSize: '10px' }}></i>
                                                                        {data.halfSoldText || 'Half Sold'}
                                                                    </span>
                                                                );
                                                                return (
                                                                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                                                        <i className="fas fa-check-circle" style={{ fontSize: '10px' }}></i>
                                                                        {data.availableText || 'Available'}
                                                                    </span>
                                                                );
                                                            })()}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Ticket Buy Button */}
                                                <div style={{
                                                    marginTop: '20px',
                                                    paddingTop: '20px',
                                                    borderTop: '1px dashed rgba(0, 0, 0, 0.1)'
                                                }}>
                                                    <button
                                                        onClick={() => {
                                                            console.log('=== EVENT TICKET BUTTON CLICK DEBUG ===');
                                                            console.log('eventData:', eventData);
                                                            console.log('ticket:', ticket);
                                                            console.log('data.useStripeCheckout:', data.useStripeCheckout);
                                                            console.log('isFreeEvent():', isFreeEvent());
                                                            console.log('eventData?.stripePriceId:', eventData?.stripePriceId);
                                                            console.log('===================================');
                                                            
                                                            if (data.buttonLink) {
                                                                // Custom link has priority
                                                                console.log('→ Opening custom button link');
                                                                window.open(data.buttonLink, '_blank');
                                                            } else if (ticket.price === 0 || ticket.price === '0') {
                                                                // Free ticket - show form popup
                                                                console.log('→ Opening form popup for free event ticket');
                                                                const event = new CustomEvent('triggerProductFormPopup', {
                                                                    detail: { 
                                                                        productId: data.eventId,
                                                                        trigger: 'eventInfoWidget',
                                                                        productData: eventData,
                                                                        ticketType: ticket.name || 'Free Ticket'
                                                                    }
                                                                });
                                                                window.dispatchEvent(event);
                                                            } else if (data.useStripeCheckout && eventData?.enableStripePayment !== false) {
                                                                if (eventData?.stripePriceId) {
                                                                    // Paid ticket with Stripe checkout enabled
                                                                    console.log('→ Triggering Stripe checkout for ticket');
                                                                    handleStripeCheckout(ticket.name || 'General');
                                                                } else {
                                                                    // Demo mode for testing
                                                                    console.log('→ DEMO MODE: Using test Stripe configuration');
                                                                    alert('DEMO MODE: Would normally redirect to Stripe Checkout now!\n\nIn real mode, you need to configure:\n- Stripe Product ID (e.g. prod_ABC123)\n- Stripe Price ID (e.g. price_XYZ789)\n\nFor now, simulating Stripe redirect...');
                                                                }
                                                            } else if (eventData?.enableFormOnly !== false) {
                                                                // Event configured for form-only - show form popup
                                                                console.log('→ Event configured for form-only - showing form popup');
                                                                const event = new CustomEvent('triggerProductFormPopup', {
                                                                    detail: { 
                                                                        productId: data.eventId,
                                                                        trigger: 'eventInfoWidget',
                                                                        productData: eventData,
                                                                        ticketType: ticket.name || 'General Ticket',
                                                                        paymentMethod: 'form-only'
                                                                    }
                                                                });
                                                                window.dispatchEvent(event);
                                                            } else {
                                                                // Pay on site / custom handling
                                                                console.log('→ Pay on site - showing form popup');
                                                                const event = new CustomEvent('triggerProductFormPopup', {
                                                                    detail: { 
                                                                        productId: data.eventId,
                                                                        trigger: 'eventInfoWidget',
                                                                        productData: eventData,
                                                                        ticketType: ticket.name || 'General Ticket',
                                                                        paymentMethod: 'on-site'
                                                                    }
                                                                });
                                                                window.dispatchEvent(event);
                                                            }
                                                        }}
                                                        style={{
                                                            ...getButtonEffectStyles(data.buttonEffect),
                                                            backgroundColor: data.buttonColor,
                                                            color: data.buttonTextColor,
                                                            border: 'none',
                                                            borderRadius: '8px',
                                                            padding: '12px 24px',
                                                            fontSize: '14px',
                                                            fontWeight: '600',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.3s ease',
                                                            fontFamily: data.buttonFont || fonts.globalSmallFont,
                                                            boxShadow: `0 2px 8px ${data.buttonColor}40`,
                                                            letterSpacing: '0.3px',
                                                            width: '100%',
                                                            textTransform: 'uppercase'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.target.style.transform = 'translateY(-1px)';
                                                            e.target.style.boxShadow = `0 4px 12px ${data.buttonColor}50`;
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.target.style.transform = 'translateY(0)';
                                                            e.target.style.boxShadow = `0 2px 8px ${data.buttonColor}40`;
                                                        }}
                                                    >
                                                        {ticket.price === 0 || ticket.price === '0' ? 
                                                            (data.showFreeTicketButtonText !== false ? (data.freeTicketButtonText || 'Get Free Ticket') : 'Get Free Ticket') : 
                                                            (data.showBuyTicketButtonText !== false ? (data.buyTicketButtonText || 'Buy Ticket') : 'Buy Ticket')
                                                        }
                                                    </button>
                                                </div>

                                                {/* Ticket Footer */}
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    marginTop: '12px'
                                                }}>
                                                    {ticket.maxQuantity && (
                                                        <div style={{
                                                            padding: '4px 8px',
                                                            backgroundColor: 'rgba(245, 158, 11, 0.15)',
                                                            borderRadius: '4px',
                                                            fontSize: '10px',
                                                            color: '#92400e',
                                                            fontWeight: '600',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '4px',
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.5px'
                                                        }}>
                                                            <i className="fas fa-bolt" style={{ fontSize: '8px' }}></i>
                                                            <span>{data.limitedText || 'Limited'}: {ticket.maxQuantity}</span>
                                                        </div>
                                                    )}
                                                    
                                                    <div style={{
                                                        fontSize: '10px',
                                                        color: data.locationColor,
                                                        opacity: 0.6,
                                                        fontFamily: 'monospace',
                                                        letterSpacing: '1px'
                                                    }}>
                                                        #{String(index + 1).padStart(3, '0')}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Mobile: Tickets under main content */}
                {isMobileLayout && data.showTickets && eventData.contentData?.tickets?.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '8px',
                                backgroundColor: `${data.ticketPriceColor}15`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: data.ticketPriceColor
                            }}>
                                <i className="fas fa-ticket-alt" style={{ fontSize: '14px' }}></i>
                            </div>
                            <h3 
                                style={{ 
                                    margin: '0', 
                                    fontSize: '20px', 
                                    fontWeight: '600',
                                    color: data.titleColor,
                                    letterSpacing: '-0.01em',
                                    cursor: isEditMode ? 'text' : 'default',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    transition: 'all 0.2s ease'
                                }}
                                onClick={() => isEditMode && handleInlineEdit('ticketsTitleMobile', data.ticketsTitle || 'Tickets')}
                                title={isEditMode ? "Click to edit" : undefined}
                            >
                                {isInlineEditing === 'ticketsTitleMobile' ? (
                                    <input
                                        type="text"
                                        value={inlineValue}
                                        onChange={(e) => setInlineValue(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleInlineSave()}
                                        onBlur={handleInlineSave}
                                        style={{
                                            fontSize: '20px',
                                            fontWeight: '600',
                                            color: data.titleColor,
                                            letterSpacing: '-0.01em',
                                            background: 'transparent',
                                            border: 'none',
                                            outline: 'none',
                                            margin: '0',
                                            padding: '0',
                                            width: '100%',
                                            fontFamily: 'inherit'
                                        }}
                                        autoFocus
                                    />
                                ) : (
                                    data.ticketsTitle || 'Tickets'
                                )}
                            </h3>
                        </div>
                        
                        <div style={{ 
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '16px'
                        }}>
                            {eventData.contentData.tickets.slice(0, data.maxTicketsToShow || 4).map((ticket, index) => (
                                <div key={index} style={{
                                    padding: '24px',
                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                    border: '1px solid rgba(0, 0, 0, 0.08)',
                                    borderRadius: '16px',
                                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)'
                                }}>
                                    {/* Same ticket content as desktop */}
                                    <div style={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        alignItems: 'flex-start', 
                                        marginBottom: '16px',
                                        gap: '12px'
                                    }}>
                                        <h4 style={{ 
                                            margin: '0', 
                                            fontSize: '18px',
                                            fontWeight: '700',
                                            color: data.titleColor,
                                            letterSpacing: '-0.01em',
                                            flex: 1
                                        }}>
                                            {ticket.name}
                                        </h4>
                                        <div style={{
                                            padding: '8px 16px',
                                            backgroundColor: `${data.ticketPriceColor}10`,
                                            borderRadius: '12px',
                                            border: `1px solid ${data.ticketPriceColor}20`
                                        }}>
                                            <span style={{
                                                fontSize: `${data.ticketPriceSize - 2}px`,
                                                fontWeight: data.ticketPriceWeight,
                                                color: data.ticketPriceColor
                                            }}>
                                                {formatTicketPrice(ticket.price, ticket.currency)}
                                            </span>
                                        </div>
                                    </div>
                                    
                                    {ticket.description && (
                                        <p style={{ 
                                            margin: '0 0 16px 0', 
                                            fontSize: '14px',
                                            color: data.descriptionColor,
                                            opacity: 0.8,
                                            lineHeight: '1.5'
                                        }}>
                                            {ticket.description}
                                        </p>
                                    )}
                                    
                                    {ticket.benefits?.length > 0 && (
                                        <div style={{ 
                                            display: 'flex', 
                                            flexDirection: 'column', 
                                            gap: '8px',
                                            marginBottom: '16px'
                                        }}>
                                            {ticket.benefits.map((benefit, benefitIndex) => (
                                                <div key={benefitIndex} style={{ 
                                                    fontSize: '13px',
                                                    color: data.locationColor,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px'
                                                }}>
                                                    <span style={{
                                                        width: '16px',
                                                        height: '16px',
                                                        borderRadius: '50%',
                                                        backgroundColor: `${data.featuresIconColor}20`,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: '10px',
                                                        color: data.featuresIconColor,
                                                        fontWeight: '700'
                                                    }}><i className="fas fa-check"></i></span>
                                                    <span>{benefit}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    
                                    {/* Mobile Ticket Buy Button */}
                                    <div style={{
                                        marginTop: '20px',
                                        paddingTop: '20px',
                                        borderTop: '1px dashed rgba(0, 0, 0, 0.1)'
                                    }}>
                                        <button
                                            onClick={() => {
                                                if (isEditMode) return;
                                                
                                                console.log('=== EVENT TICKET MOBILE BUTTON CLICK DEBUG ===');
                                                console.log('eventData:', eventData);
                                                console.log('ticket:', ticket);
                                                console.log('data.useStripeCheckout:', data.useStripeCheckout);
                                                console.log('isFreeEvent():', isFreeEvent());
                                                console.log('eventData?.stripePriceId:', eventData?.stripePriceId);
                                                console.log('==========================================');
                                                
                                                if (data.buttonLink) {
                                                    // Custom link has priority
                                                    console.log('→ Opening custom button link');
                                                    window.open(data.buttonLink, '_blank');
                                                } else if (ticket.price === 0 || ticket.price === '0') {
                                                    // Free ticket - show form popup
                                                    console.log('→ Opening form popup for free event ticket');
                                                    const event = new CustomEvent('triggerProductFormPopup', {
                                                        detail: { 
                                                            productId: data.eventId,
                                                            trigger: 'eventInfoWidget',
                                                            productData: eventData,
                                                            ticketType: ticket.name || 'Free Ticket'
                                                        }
                                                    });
                                                    window.dispatchEvent(event);
                                                } else if (data.useStripeCheckout && eventData?.enableStripePayment !== false) {
                                                    if (eventData?.stripePriceId) {
                                                        // Paid ticket with Stripe checkout enabled
                                                        console.log('→ Triggering Stripe checkout for ticket');
                                                        handleStripeCheckout(ticket.name || 'General');
                                                    } else {
                                                        // Demo mode for testing
                                                        console.log('→ DEMO MODE: Using test Stripe configuration');
                                                        alert('DEMO MODE: Would normally redirect to Stripe Checkout now!\n\nIn real mode, you need to configure:\n- Stripe Product ID (e.g. prod_ABC123)\n- Stripe Price ID (e.g. price_XYZ789)\n\nFor now, simulating Stripe redirect...');
                                                    }
                                                } else if (eventData?.enableFormOnly !== false) {
                                                    // Event configured for form-only - show form popup
                                                    console.log('→ Event configured for form-only - showing form popup');
                                                    const event = new CustomEvent('triggerProductFormPopup', {
                                                        detail: { 
                                                            productId: data.eventId,
                                                            trigger: 'eventInfoWidget',
                                                            productData: eventData,
                                                            ticketType: ticket.name || 'General Ticket',
                                                            paymentMethod: 'form-only'
                                                        }
                                                    });
                                                    window.dispatchEvent(event);
                                                } else {
                                                    // Pay on site / custom handling
                                                    console.log('→ Pay on site - showing form popup');
                                                    const event = new CustomEvent('triggerProductFormPopup', {
                                                        detail: { 
                                                            productId: data.eventId,
                                                            trigger: 'eventInfoWidget',
                                                            productData: eventData,
                                                            ticketType: ticket.name || 'General Ticket',
                                                            paymentMethod: 'on-site'
                                                        }
                                                    });
                                                    window.dispatchEvent(event);
                                                }
                                            }}
                                            style={{
                                                ...getButtonEffectStyles(data.buttonEffect),
                                                backgroundColor: data.buttonColor,
                                                color: data.buttonTextColor,
                                                border: 'none',
                                                borderRadius: '8px',
                                                padding: '12px 24px',
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                cursor: 'pointer',
                                                transition: 'all 0.3s ease',
                                                fontFamily: data.buttonFont || fonts.globalSmallFont,
                                                boxShadow: `0 2px 8px ${data.buttonColor}40`,
                                                letterSpacing: '0.3px',
                                                width: '100%',
                                                textTransform: 'uppercase'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.target.style.transform = 'translateY(-1px)';
                                                e.target.style.boxShadow = `0 4px 12px ${data.buttonColor}50`;
                                            }}
                                            onMouseLeave={(e) => {
                                                e.target.style.transform = 'translateY(0)';
                                                e.target.style.boxShadow = `0 2px 8px ${data.buttonColor}40`;
                                            }}
                                        >
                                            {ticket.price === 0 || ticket.price === '0' ? 
                                                (data.showFreeTicketButtonText !== false ? (data.freeTicketButtonText || 'Get Free Ticket') : 'Get Free Ticket') : 
                                                (data.showBuyTicketButtonText !== false ? (data.buyTicketButtonText || 'Buy Ticket') : 'Buy Ticket')
                                            }
                                        </button>
                                    </div>

                                    {/* Mobile Ticket Footer */}
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginTop: '12px'
                                    }}>
                                        {ticket.maxQuantity && (
                                            <div style={{
                                                padding: '4px 8px',
                                                backgroundColor: 'rgba(245, 158, 11, 0.15)',
                                                borderRadius: '4px',
                                                fontSize: '10px',
                                                color: '#92400e',
                                                fontWeight: '600',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px'
                                            }}>
                                                <i className="fas fa-bolt" style={{ fontSize: '8px' }}></i>
                                                <span>Limited: {ticket.maxQuantity}</span>
                                            </div>
                                        )}
                                        
                                        <div style={{
                                            fontSize: '10px',
                                            color: data.locationColor,
                                            opacity: 0.6,
                                            fontFamily: 'monospace',
                                            letterSpacing: '1px'
                                        }}>
                                            #{String(index + 1).padStart(3, '0')}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* CSS Animations */}
            <style jsx>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                
                @keyframes goldenShimmer {
                    0%, 100% { 
                        filter: brightness(1) saturate(1) hue-rotate(0deg); 
                        transform: perspective(1000px) rotateX(3deg) rotateY(-2deg);
                    }
                    50% { 
                        filter: brightness(1.08) saturate(1.15) hue-rotate(3deg); 
                        transform: perspective(1000px) rotateX(3deg) rotateY(-2deg) scale(1.003);
                    }
                }
                
                @keyframes silverShimmer {
                    0%, 100% { 
                        filter: brightness(1) contrast(1); 
                        transform: perspective(1000px) rotateX(2deg) rotateY(1deg);
                    }
                    50% { 
                        filter: brightness(1.05) contrast(1.08); 
                        transform: perspective(1000px) rotateX(2deg) rotateY(1deg) scale(1.002);
                    }
                }
                
                @keyframes premiumGlow {
                    0%, 100% { 
                        box-shadow: 0 12px 40px rgba(139, 92, 246, 0.4), 0 8px 24px rgba(168, 85, 247, 0.3), inset 0 2px 0 rgba(255, 255, 255, 0.4), inset 0 -1px 0 rgba(139, 92, 246, 0.3);
                        transform: perspective(1000px) rotateX(2deg) rotateY(-1deg);
                    }
                    50% { 
                        box-shadow: 0 14px 44px rgba(139, 92, 246, 0.45), 0 10px 28px rgba(168, 85, 247, 0.35), inset 0 2px 0 rgba(255, 255, 255, 0.45), inset 0 -1px 0 rgba(139, 92, 246, 0.35);
                        transform: perspective(1000px) rotateX(2deg) rotateY(-1deg) scale(1.003);
                    }
                }
                
                @keyframes vipPulse {
                    0%, 100% { 
                        transform: perspective(1000px) rotateX(3deg) rotateY(2deg) scale(1);
                        box-shadow: 0 12px 40px rgba(220, 38, 38, 0.4), 0 8px 24px rgba(239, 68, 68, 0.3), inset 0 2px 0 rgba(255, 255, 255, 0.3), inset 0 -1px 0 rgba(220, 38, 38, 0.3);
                    }
                    50% { 
                        transform: perspective(1000px) rotateX(3deg) rotateY(2deg) scale(1.005);
                        box-shadow: 0 14px 44px rgba(220, 38, 38, 0.45), 0 10px 28px rgba(239, 68, 68, 0.35), inset 0 2px 0 rgba(255, 255, 255, 0.35), inset 0 -1px 0 rgba(220, 38, 38, 0.35);
                    }
                }
                
                @keyframes progressPulse {
                    0%, 100% { opacity: 0.6; }
                    50% { opacity: 1; }
                }
                
                @keyframes blink {
                    0%, 50% { opacity: 1; }
                    51%, 100% { opacity: 0; }
                }
                
                .event-info-widget:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12), 0 8px 24px rgba(0, 0, 0, 0.08);
                }
            `}</style>
        </div>
    );
};

export default EventInfoWidget;
